import React from 'react';
import styled from 'styled-components';
import CloseIcon from '@material-ui/icons/Close';

interface Props {
  /** What Preview PDF will stamp here — printedLabel, not the ancestor path. */
  label: string;
  /** Full path, for the tooltip: the label alone can be ambiguous. */
  title?: string;
  selected: boolean;
  onDelete: () => void;
  /**
   * The size, in view pixels, that this label will actually print at.
   * See PlaceholderCanvas for why the chip draws it rather than a fixed 12px.
   */
  fontPx: number;
}

// Presentational chip rendered as the child of an <Rnd> by PlaceholderCanvas.
// Fills the Rnd box (100% w/h). Mirrors the Inspection Form Builder's FieldChip.
const PlaceholderChip = ({ label, title, selected, onDelete, fontPx }: Props) => (
  <Chip $selected={selected} title={title || label}>
    <Text $fontPx={fontPx}>{label}</Text>
    {selected && (
      <DeleteBtn
        // react-draggable-cancel + stopPropagation, or clicking the badge
        // starts a drag instead of deleting.
        className="react-draggable-cancel"
        onMouseDown={e => e.stopPropagation()}
        onClick={e => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label={`Remove ${label}`}
        title="Remove this placeholder"
      >
        <CloseIcon style={{ fontSize: 12 }} />
      </DeleteBtn>
    )}
  </Chip>
);

export default PlaceholderChip;

// Matches the Inspection Form Builder's field skin (FormFillCanvas's
// FieldInput / FieldArea): a translucent light-blue wash rather than a solid
// fill. Deliberately rgba and not the --primary/--neutral tokens — these boxes
// sit ON TOP of the form page, so they have to let the pre-printed lines and
// labels show through. The old `--neutral-50` was opaque and hid whatever it
// was placed over, which is exactly what you need to see when positioning.
const CHIP_FILL = 'rgba(11, 107, 155, 0.08)';
const CHIP_FILL_HOVER = 'rgba(11, 107, 155, 0.16)';
const CHIP_FILL_SELECTED = 'rgba(11, 107, 155, 0.14)';
const CHIP_EDGE = 'rgba(11, 107, 155, 0.35)';

const Chip = styled.div<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  /* TOP-LEFT, not centred. The renderer hangs each value off the stored
     corner — "left: x pt; top: y pt" on an absolutely positioned div
     (pdf_render.py::_instruction_html) — so a label floating in the middle of
     the box pointed at a spot the text will never occupy. On a 175x28 box that
     is 87pt to the right and 9pt below where the value actually prints, which
     is exactly the "Email is not mapped correctly" mismatch: the chip sat on
     the Permit # rule while the printed word landed above and left of it.
     Anchoring here makes the chip's own top-left corner the thing you aim,
     and it is the same corner the panel's X / Y boxes edit. */
  align-items: flex-start;
  justify-content: flex-start;
  /* 1px, matching the border, so the glyph origin lands on the stored
     coordinate rather than 4px inside it. */
  padding: 0 1px;
  cursor: move;
  border-radius: 3px;
  border: 1px solid
    ${({ $selected }) => ($selected ? 'var(--primary-600)' : CHIP_EDGE)};
  background: ${({ $selected }) =>
    ($selected ? CHIP_FILL_SELECTED : CHIP_FILL)};
  box-shadow: ${({ $selected }) => ($selected ? 'var(--shadow-focus)' : 'none')};
  transition: background-color 0.1s ease, border-color 0.1s ease;

  &:hover {
    border-color: var(--primary-600);
    background: ${({ $selected }) =>
    ($selected ? CHIP_FILL_SELECTED : CHIP_FILL_HOVER)};
  }
`;

// Deliberately mirrors the server's own text rule (pdf_render.py):
//
//   .text { font-family: 'FormSetupSans', Helvetica, Arial, sans-serif;
//           line-height: 1.15; white-space: pre; }
//
// FormSetupSans IS arial.ttf, so Arial first here gives the same metrics and
// therefore the same run length on screen as on the page.
const Text = styled.span<{ $fontPx: number }>`
  font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
  /* The size it will PRINT at, not a fixed 12px. A form set to 24 px prints at
     ~8.6pt, so a constant 12px label overstated every value by 40% and made
     long fields look like they fit when they will not. */
  font-size: ${({ $fontPx }) => $fontPx}px;
  line-height: ${({ $fontPx }) => $fontPx * 1.15}px;
  color: var(--neutral-900);
  white-space: pre;
  /* No ellipsis, and no clipping. The renderer neither wraps nor clips — the
     panel's hint says so — so text that runs past the box on screen is text
     that will run past it on the page. Hiding the overflow would hide the one
     thing worth seeing. */
  overflow: visible;
  pointer-events: none;
  user-select: none;
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: -9px;
  right: -9px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--danger-500, #d44b3f);
  color: #fff;
  cursor: pointer;
  box-shadow: var(--shadow-xs);
  z-index: 3;

  &:hover {
    background: var(--danger-600, #b83a30);
  }
`;
